
import { Component, defineComponent, PropType } from "vue";
import { ModalSize, MODAL_DELAY } from "../models/model";

interface Data {
  show: boolean;
  out: boolean;
  pulse: boolean;
  initialTarget: HTMLElement | undefined;
  disableScrolling: boolean;
}

const MODAL_CENTER_CLASS = "modal-dialog-centered";
const MODAL_PULSE_CLASS = "bs-dialog-animated dialog-pulse";
const MODAL_SHOW_CLASS = "bs-dialog-show";
const DISABLE_SCROLLING = "hidden";
const ENABLE_SCROLLING = "";

export default defineComponent({
  name: "VBSModal",
  data(): Data {
    return {
      show: false,
      out: false,
      pulse: false,
      initialTarget: undefined,
      disableScrolling: false,
    };
  },
  props: {
    title: {
      type: String as PropType<string>,
      default: "Confirmation",
    },
    message: {
      type: String as PropType<string>,
      default: "Do you want to do this action?",
    },
    icon: {
      type: String as PropType<string>,
      default: "bi bi-question-circle-fill",
    },
    size: {
      type: String as PropType<ModalSize>,
    },
    leftBtnText: {
      type: String as PropType<string>,
      default: "Cancel",
    },
    rightBtnText: {
      type: String as PropType<string>,
      default: "Confirm",
    },
    center: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    backgroundScrolling: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    staticBackdrop: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    displayCloseBtn: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    content: {
      type: [Object, String] as PropType<Component | string>,
    },
    contentProps: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },
    contentEmits: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },
    resolve: {
      type: Function as PropType<(value: unknown) => void>,
    },
    reject: {
      type: Function as PropType<(reason: unknown) => void>,
    },
    full: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  created() {
    this.show = true;
    if (!this.backgroundScrolling) {
      this.disableScrolling = true;
      this.toggleBodyScrolling(false);
    }
  },
  unmounted() {
    if (!this.backgroundScrolling && this.disableScrolling) {
      this.toggleBodyScrolling(true);
    }
  },
  methods: {
    onConfirm() {
      this.resolve?.(true);
      this.closeModal();
    },

    onCancel() {
      this.resolve?.(false);
      this.closeModal();
    },

    setInitialTarget(event: MouseEvent) {
      this.initialTarget = event.target as HTMLElement;
    },

    onBackdropClick(event: MouseEvent) {
      const finalTarget = event.target as HTMLElement;
      if (!finalTarget?.classList?.contains(MODAL_SHOW_CLASS)) {
        return;
      }
      if (finalTarget === this.initialTarget) {
        if (this.staticBackdrop) {
          this.pulse = true;
          setTimeout(() => (this.pulse = false), MODAL_DELAY);
          return;
        }
        this.resolve?.(false);
        this.closeModal();
      }
    },

    onCloseBtnClick() {
      this.resolve?.(false);
      this.closeModal();
    },

    closeModal() {
      this.$vbsModal?.close();
    },

    toggleBodyScrolling(scroll: boolean) {
      scroll
        ? (document.body.style.overflow = ENABLE_SCROLLING)
        : (document.body.style.overflow = DISABLE_SCROLLING);
    },

    getDialogClass() {
      let clazz;
      if (this.size) {
        clazz = {
          [this.size]: true,
        };
      }
      clazz = {
        ...clazz,
        [MODAL_PULSE_CLASS]: this.pulse,
        [MODAL_CENTER_CLASS]: this.center,
      };
      return clazz;
    },

    getDialogContainerClass() {
      return {
        [MODAL_SHOW_CLASS]: this.show,
      };
    },

    getDialogBackdropClass() {
      return {
        [MODAL_SHOW_CLASS]: this.show,
      };
    },
  },
});
